// components/features/categories/CategoryForm.tsx
import { useState, useEffect } from 'react';
import { Category, Sector } from '@cabukaka/types';
import { Tag, Layers, Image as ImageIcon, Trash2 } from 'lucide-react';

interface CategoryFormProps {
  initialData?: Partial<Category>;
  sectors: Sector[];
  onSubmit: (data: { name: string; sectorId: string; imageFile?: File; existingImage?: string }) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormErrors {
  name?: string;
  sectorId?: string;
  image?: string;
}

export default function CategoryForm({
  initialData,
  sectors,
  onSubmit,
  isSubmitting = false
}: CategoryFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [sectorId, setSectorId] = useState(initialData?.sectorId || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImage, setExistingImage] = useState<string>(initialData?.image || '');
  const [imagePreview, setImagePreview] = useState<string>(initialData?.image || '');
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [imageFile]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setExistingImage('');
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setExistingImage('');
    setImagePreview('');
  };

 

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name) newErrors.name = 'Nom de la catégorie requis';
    if (!sectorId) newErrors.sectorId = 'Secteur requis';
    if (!imageFile && !existingImage) newErrors.image = 'Image requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    await onSubmit({
      name,
      sectorId,
      imageFile: imageFile || undefined,
      existingImage: existingImage || undefined
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image *</label>
        <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50">
          {imagePreview ? (
            <>
              <img src={imagePreview} alt="Image" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          ) : (
            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
              <ImageIcon className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">Ajouter</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </label>
          )}
        </div>
        {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
      </div>

      {/* Nom */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la catégorie *</label>
        <div className="relative">
          <Tag className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
            placeholder="Ex: Smartphones, PC, Accessoires"
          />
        </div>
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Secteur */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Secteur *</label>
        <div className="relative">
          <Layers className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <select
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.sectorId ? 'border-red-300' : 'border-gray-300'}`}
          >
            <option value="">Sélectionner un secteur</option>
            {sectors.map(sector => (
              <option key={sector.id} value={sector.id}>{sector.name}</option>
            ))}
          </select>
        </div>
        {errors.sectorId && <p className="text-red-500 text-xs mt-1">{errors.sectorId}</p>}
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50"
        >
          {isSubmitting ? 'Enregistrement...' : (initialData?.id ? 'Modifier' : 'Créer')}
        </button>
      </div>
    </form>
  );
}